/**
 * Task/automation API calls.
 *
 * Tasks are asynchronous jobs (model executions, imports, etc.)
 * that run on the server and can be monitored from the UI.
 */
import apiClient from './client';
import { buildAuditSearchUrl, type AuditSearchParams } from './auditSearch';
import type { ComponentException } from './jobs';
import type { Task, PaginatedResponse, SkipLimitSearchResponse, SearchParams } from '@/types';

/**
 * Which tasks a profile's or a model's task listing holds (jorigin/brewlytics#2204): the caller's
 * own, the ones shared with them (their READ or EXECUTE names them or a group they inherit), or
 * both. The server narrows the query itself, so every page and its `total` hold only that scope.
 */
export type TaskScope = 'mine' | 'shared' | 'all';

/** List tasks. */
export async function listTasks(params?: SearchParams): Promise<PaginatedResponse<Task>> {
  const res = await apiClient.get<PaginatedResponse<Task>>('api/tasks', { params });
  return res.data;
}

/**
 * Search tasks via the skip/limit endpoint.
 *
 * Example: `api/tasks/search?limit=10&text=*&skip=0`
 * Response shape: `{ total, data, limit, skip }`.
 */
export async function searchTasks(params: SearchParams): Promise<SkipLimitSearchResponse<Task>> {
  const res = await apiClient.get<SkipLimitSearchResponse<Task>>('api/tasks/search', { params });
  return res.data;
}

/**
 * Search tasks scoped to a specific model.
 *
 * Example: `api/models/:modelId/tasks/search?limit=10&skip=0&sort=-metadata.lastUpdated,-metadata.dateCreated`
 *
 * The `sort` value contains commas that the server treats as a multi-field
 * sort spec — they must NOT be percent-encoded, so we build the URL by hand
 * rather than letting axios serialize a params object.
 */
export async function searchModelTasks(
  modelId: string,
  params: { limit?: number; skip?: number; sort?: string; text?: string; scope?: TaskScope } = {},
): Promise<SkipLimitSearchResponse<Task>> {
  const {
    limit = 10,
    skip = 0,
    sort = '-metadata.lastUpdated,-metadata.dateCreated',
    text,
    scope,
  } = params;
  const qs = new URLSearchParams();
  qs.set('limit', String(limit));
  qs.set('skip', String(skip));
  if (text) qs.set('text', text);
  // None is every task of the model the caller may open — `all`.
  if (scope) qs.set('scope', scope);
  // Append sort manually to keep its commas literal.
  const url = `api/models/${encodeURIComponent(modelId)}/tasks/search?${qs.toString()}&sort=${sort}`;
  const res = await apiClient.get<SkipLimitSearchResponse<Task>>(url);
  return res.data;
}

/**
 * List the tasks bound to a specific model.
 *
 *   GET api/models/:modelId/tasks?summary=false
 *
 * `summary=false` asks the server to return the full task objects (label,
 * schedule, parameters, $status, etc.) rather than the bare id/uri summary.
 */
export async function getModelTasks(modelId: string): Promise<Task[]> {
  const res = await apiClient.get<Task[]>(
    `api/models/${encodeURIComponent(modelId)}/tasks`,
    { params: { summary: false } },
  );
  return res.data ?? [];
}

/**
 * Search tasks scoped to a profile.
 *
 * Example: `api/profiles/:profileId/tasks/search?limit=10&skip=0&sort=-metadata.lastUpdated&text=*`
 *
 * Sort is appended manually (rather than via axios params) so any commas in a
 * multi-field spec stay literal — same pattern as `searchModelTasks`.
 *
 * `withLastRun` (jorigin/brewlytics#2303): each of the CALLER's own tasks on
 * the page carries its last run (`$lastRun`), looked up once for the page —
 * not a history request per task. A task someone else owns never carries one.
 * The server needs a `limit` of 1 to 50 with it.
 */
export async function searchProfileTasks(
  profileId: string,
  params: { limit?: number; skip?: number; sort?: string; text?: string; scope?: TaskScope; withLastRun?: boolean } = {},
): Promise<SkipLimitSearchResponse<Task>> {
  const {
    limit = 10,
    skip = 0,
    sort = '-metadata.lastUpdated',
    text,
    scope,
    withLastRun = false,
  } = params;
  const qs = new URLSearchParams();
  qs.set('limit', String(limit));
  qs.set('skip', String(skip));
  if (text) qs.set('text', text);
  // None is the profile's own tasks — `mine`.
  if (scope) qs.set('scope', scope);
  if (withLastRun) qs.set('withLastRun', 'true');
  const url = `api/profiles/${encodeURIComponent(profileId)}/tasks/search?${qs.toString()}&sort=${sort}`;
  const res = await apiClient.get<SkipLimitSearchResponse<Task>>(url);
  return res.data;
}

/** Toggle the `enabled` flag on a task. */
export async function setTaskEnabled(id: string, enabled: boolean): Promise<void> {
  await apiClient.put(`api/tasks/${id}/enabled`, { _id: id, enabled });
}

/** One bucket from `api/tasks/:id/audits/histogram`. */
export interface AuditHistogramBucket {
  ts: number;
  at?: string;
  total?: number;
  read?: number;
  create?: number;
  update?: number;
  delete?: number;
  grant?: number;
  execute?: number;
  search?: number;
}

/** Response from `api/tasks/:id/audits/histogram`. */
export interface AuditHistogramResponse {
  duration: string;
  values: AuditHistogramBucket[];
  epoch: string;
  units: string;
  type: string;
}

/** Get the per-bucket audit histogram for a task. */
export async function getTaskAuditHistogram(
  taskId: string,
  params: { days?: number; samples?: number } = {},
): Promise<AuditHistogramResponse> {
  const { days = 15, samples = 30 } = params;
  const res = await apiClient.get<AuditHistogramResponse>(
    `api/tasks/${taskId}/audits/histogram`,
    { params: { days, samples } },
  );
  return res.data;
}

// ── Audit (execution) history ────────────────────────────────────────────

export interface AuditParameterValue {
  value?: unknown;
  valueExt?: unknown;
}

export interface AuditComponentOutput {
  value?: unknown;
  length?: number;
  /** Some output payloads include nested fields beyond value/length. */
  [key: string]: unknown;
}

export interface AuditComponent {
  status?: string;
  outputs?: Record<string, AuditComponentOutput>;
  /** What the component threw, on a run that failed (jorigin/brewlytics#2304). */
  exception?: ComponentException;
}

export interface AuditResults {
  id?: string;
  status?: string;
  serverTs?: string;
  startTime?: string;
  endTime?: string;
  ttlTime?: string;
  elapsedTimeMs?: number;
  createdBy?: string;
  components?: Record<string, AuditComponent>;
  /** Model validation refusals of an ERRORED job, each `{ "<id>": "<message>" }`. */
  validationErrors?: Array<Record<string, string>>;
}

export interface AuditAttributes {
  duration?: string;
  /** Who the run ran as: the server's principal string, `"<subject>/<issuer> (<class>)"`. */
  runAs?: string;
  /** The task owner's profile id, on rows written since jorigin/brewlytics#2207. */
  ownerId?: string;
  /**
   * How the run ended — COMPLETED, SKIPPED, FAILED, or the job's own end state. Recorded since
   * jorigin/brewlytics#2207, and derived by the server for a redacted row of an older run.
   */
  outcome?: string;
  modelId?: string;
  /** The credential that recorded the row (for a task run, the scheduler's) — not the run's user. */
  dn?: string;
  parameters?: Record<string, AuditParameterValue>;
  results?: AuditResults;
  /**
   * A run's component errors, flattened ("Description: …", "Trace: […]"). Like `error`, sent only
   * to the task's owner, an administrator or an Auditor (jorigin/brewlytics#2207).
   */
  errors?: unknown[];
  /** Why a run failed or was skipped before its job ended. */
  error?: string;
  [key: string]: unknown;
}

export interface AuditEntry {
  subject?: string;
  verb?: string;
  attributes?: AuditAttributes;
  timestamp?: string;
  object?: string;
}

/**
 * Search task audit (execution) history.
 *
 * The `^` prefix on `auditType` is a legacy server convention for predicate
 * filters and must remain unencoded — we build the URL by hand so axios
 * doesn't percent-encode the caret.
 */
export async function searchTaskAudits(
  taskId: string,
  params: { auditType?: string; limit?: number; skip?: number } = {},
): Promise<SkipLimitSearchResponse<AuditEntry>> {
  const { auditType = 'EXECUTE', limit = 10, skip = 0 } = params;
  const url =
    `api/tasks/${taskId}/audits/search` +
    `?^auditType=${encodeURIComponent(auditType)}` +
    `&limit=${limit}&skip=${skip}`;
  const res = await apiClient.get<SkipLimitSearchResponse<AuditEntry>>(url);
  return res.data;
}

export type TaskAuditType = 'EXECUTE' | 'CREATE' | 'READ' | 'UPDATE' | 'DELETE';

/**
 * Search the global task audit log (across every task).
 *
 *   GET api/tasks/audits/search?^auditType=EXECUTE&text=*&limit=20&skip=0&sort=-timestamp
 *
 * Same URL-builder pattern as searchProfileAudits / searchModelAudits /
 * searchFunctionalAudits — the `^auditType` predicate prefix stays unencoded.
 * Omit `auditType` to fetch every verb at once. This is the admin-wide
 * counterpart to the per-task `searchTaskAudits(taskId, …)` used by the
 * task detail history view.
 */
export async function searchAllTaskAudits(
  params: AuditSearchParams<TaskAuditType> = {},
): Promise<SkipLimitSearchResponse<AuditEntry>> {
  const url = buildAuditSearchUrl('api/tasks/audits/search', params);
  const res = await apiClient.get<SkipLimitSearchResponse<AuditEntry>>(url);
  return res.data;
}

/** Get a specific task. */
export async function getTask(id: string): Promise<Task> {
  const res = await apiClient.get<Task>(`api/tasks/${id}`);
  return res.data;
}

/** Create a new task. */
export async function createTask(data: Partial<Task>): Promise<Task> {
  const res = await apiClient.post<Task>('api/tasks', data);
  return res.data;
}

/**
 * Save (upsert) a task scoped to a model. The legacy server treats PUT to
 * `api/models/:modelId/tasks` as create-or-update keyed by `_id` — the same
 * call handles both paths. Used in place of POST `api/tasks` so that the
 * server can harvest model defaults and resolve parameter overrides.
 */
export async function saveModelTask(modelId: string, data: Partial<Task>): Promise<Task> {
  const res = await apiClient.put<Task>(`api/models/${encodeURIComponent(modelId)}/tasks`, data);
  return res.data;
}

/** Update a task. */
export async function updateTask(id: string, data: Partial<Task>): Promise<Task> {
  const res = await apiClient.put<Task>(`api/tasks/${id}`, data);
  return res.data;
}

/** Delete a task. */
export async function deleteTask(id: string): Promise<void> {
  await apiClient.delete(`api/tasks/${id}`);
}

/** Get task count. */
export async function getTaskCount(): Promise<number> {
  const res = await apiClient.get<number | string>('api/tasks/count');
  return Number(res.data) || 0;
}

